import classnames from 'classnames';
import React from 'react';

interface Props {
  /**
   * Tailwind geometry only (height, width, rounding), e.g. `'h-x3 w-x20 rounded-full'`. The fill and
   * pulse are owned here so every skeleton stays consistent; callers supply just size and shape.
   * With a child it applies only while loading, for the axes the child cannot supply, such as the
   * width of a label whose text is not known yet.
   */
  className?: string;
  /** Whether to mask the component. Its layout, state, and effects remain active while loading. */
  loading?: boolean;
  /**
   * One element that forwards className to its root. Fragments and portals are not supported.
   * `null` and `false` fall back to the plain box, so `{cond && <Button />}` is safe.
   */
  children?: React.ReactElement<{className?: string}> | null | false;
}

// Match legacy atom specificity without changing their layout or using !important.
const maskClassName = classnames(
  '[&&&]:background-bold [&&&]:bg-none [&&&]:text-transparent',
  '[&&&]:border-transparent [&&&]:shadow-none [&&&]:outline-none',
  '[&&&]:pointer-events-none [&&&]:select-none [&&&]:cursor-default',
  '[&&&]:animate-pulse-subtle motion-reduce:[&&&]:animate-none',
  '[&&&]:placeholder:text-transparent [&_*]:invisible',
  '[&::before]:invisible [&::after]:invisible'
);

/**
 * # Skeleton
 *
 * A pulsing placeholder, sized manually or by a real component's rendered layout. Wrapping a
 * component masks one root, including its descendants; it does not generate separate shapes inside
 * a card. Pass representative content when its length determines the size. Child effects still run.
 * Components must forward className to one root. Portals, fragments, and media roots such as img,
 * video, or canvas are not supported. Inline or !important paint styles can override the mask.
 * The display:contents host adds no layout box, but does affect direct-child CSS selectors.
 *
 * Decorative and inert while loading. Put aria-busy and a loading status on the containing region.
 *
 * A null, false, or absent child falls back to the plain box.
 *
 * ## Usage
 *
 * ```tsx
 * import {Skeleton} from '@albert-io/atomic';
 *
 * <Skeleton className='h-x3 w-x20 rounded-full' />
 * <Skeleton loading={isLoading}><Button size='l'>Assign practice</Button></Skeleton>
 * <Skeleton className='w-[140px]'><Text as='label'>Field</Text></Skeleton>
 * ```
 */
export const Skeleton = ({className, loading = true, children}: Props) => {
  if (children == null || children === false) {
    return loading ? (
      <div
        aria-hidden='true'
        className={classnames(
          'animate-pulse-subtle background-bold motion-reduce:animate-none',
          className
        )}
      />
    ) : null;
  }

  if (!React.isValidElement<{className?: string}>(children) || children.type === React.Fragment) {
    throw new Error('Skeleton expects one element that forwards className to its root.');
  }

  return (
    <div className='contents' aria-hidden={loading || undefined} inert={loading || undefined}>
      {React.cloneElement(children, {
        className: classnames(
          children.props.className,
          loading && className,
          loading && maskClassName
        )
      })}
    </div>
  );
};

Skeleton.displayName = 'Skeleton';

export default Skeleton;
